import { supabase } from '@/lib/supabase'

export type UserProfile = {
  id: string
  full_name: string | null
  phone: string | null
  country?: string | null
  role: 'master' | 'member' | 'afiliado' | 'vip-100' | 'vip-50'
  must_change_password?: boolean
  avatar_url?: string | null
}

export async function fetchProfile(_userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase.rpc('get_my_profile')
  if (error) throw error
  const row = (Array.isArray(data) ? data[0] : data) as any
  if (!row) return null
  // Fallback: complement with auth.user_metadata.avatar_url when column is not present in view
  let metaAvatar: string | null = null
  try { metaAvatar = (await supabase.auth.getSession()).data.session?.user?.user_metadata?.avatar_url ?? null } catch {}
  return {
    id: row.id,
    full_name: row.full_name ?? null,
    phone: row.phone ?? null,
    country: row.country ?? null,
    role: (row.role || 'member') as any,
    must_change_password: !!row.must_change_password,
    avatar_url: (row.avatar_url ?? metaAvatar ?? null),
  }
}

export async function ensureProfile(_userId: string, _email?: string): Promise<UserProfile> {
  const { error } = await supabase.rpc('ensure_profile')
  if (error) throw error
  const prof = await fetchProfile('self')
  return prof as UserProfile
}

export async function updateProfile(_userId: string, patch: { full_name?: string | null; phone?: string | null; country?: string | null }) {
  // Try RPC with country support; fallback gracefully
  const tryWithCountry = await supabase.rpc('update_my_profile', { p_full_name: patch.full_name ?? null, p_phone: patch.phone ?? null, p_country: patch.country ?? null })
  if (!tryWithCountry.error) return await fetchProfile('self')
  // Fallback: legacy RPC without country
  const legacy = await supabase.rpc('update_my_profile', { p_full_name: patch.full_name ?? null, p_phone: patch.phone ?? null })
  if (!legacy.error && patch.country !== undefined) {
    try { await supabase.from('profiles').update({ country: patch.country ?? null }).eq('id', (await supabase.auth.getUser()).data.user?.id) } catch {}
  }
  if (legacy.error) throw legacy.error
  return await fetchProfile('self')
}

export async function updateAvatarUrl(userId: string, url: string | null) {
  try {
    const { error } = await supabase.from('profiles').update({ avatar_url: url }).eq('id', userId)
    if (error) throw error
  } catch {
    // Ignore and fall back to auth metadata update
  }
  try { await supabase.auth.updateUser({ data: { avatar_url: url || null } as any }) } catch {}
  return await fetchProfile('self')
}
